import {useTranslation} from "react-i18next";
import {useToast} from "../../modules/layout/components/ToastContext";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import React, {useEffect, useMemo, useState} from "react";
import {Field, Form, Formik} from "formik";
import {
    AuditActionTypeEnumText,
    AuditEntityTypeEnumText,
    AuditSourceSystemEnumText,
} from "../../helpers/EnumeratorHelper";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import {IAuditActionType, IAuditLog, IFilterAuditLogModel} from "../../interfaces/IUtilities";
import {NavLink, useNavigate} from "react-router-dom";
import {getAll} from "../_request";
import Loader from "../../modules/layout/components/Loader";
import SortDirection from "../../modules/layout/components/SortDirection";
import {FormikDatePicker} from "../../modules/layout/components/FormikDatePicker";
import Pagination from "../../modules/layout/components/Pagination";
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';
import {formatDateTime, toApiDateTime} from "../../helpers/DateTimeHelper";

const AuditLogList = () => {
    const {t} = useTranslation();
    const {showToast} = useToast();
    const navigate = useNavigate();

    const [auditLogs, setAuditLogs] = useState([] as IAuditLog[]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [asc, setAsc] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("name");
    const initialFormState = {
        userid: '',
        datefrom: '',
        dateto: '',
        userName: '',
        actionType: undefined,
        fullName: ''
    }
    let order = asc ? "asc" : "desc";
    const [activeFilters, setActiveFilters] = useState<IFilterAuditLogModel>(initialFormState);

    const handleFilterSearch = async (filters: IFilterAuditLogModel) => {
        setPage(1);
        setActiveFilters(filters);
        await fetchLogs(filters);
    };

    useEffect(() => {
        fetchLogs();
    }, [navigate, showToast, page, sortField, asc, pageSize]);

    const actionTypeOptions = useMemo(() => [
        { value: String(IAuditActionType.Action), label: AuditActionTypeEnumText[IAuditActionType.Action] },
        { value: String(IAuditActionType.Create), label: AuditActionTypeEnumText[IAuditActionType.Create] },
        { value: String(IAuditActionType.Update), label: AuditActionTypeEnumText[IAuditActionType.Update] },
        { value: String(IAuditActionType.Delete), label: AuditActionTypeEnumText[IAuditActionType.Delete] },
        { value: String(IAuditActionType.Login), label: AuditActionTypeEnumText[IAuditActionType.Login] },
        { value: String(IAuditActionType.Verify), label: AuditActionTypeEnumText[IAuditActionType.Verify] },
        { value: String(IAuditActionType.OtpEmailSent), label: AuditActionTypeEnumText[IAuditActionType.OtpEmailSent] },
        { value: String(IAuditActionType.OtpSmsSent), label: AuditActionTypeEnumText[IAuditActionType.OtpSmsSent] },
        { value: String(IAuditActionType.OtpEmailCcSent), label: AuditActionTypeEnumText[IAuditActionType.OtpEmailCcSent] },
    ], [t]);

    const fetchLogs = async (filters: IFilterAuditLogModel = activeFilters) => {
        try {

            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];
            if (filters.userid) params.push({ key: 'userid', value: filters.userid });
            if (filters.datefrom)
                params.push({key: 'datefrom', value: toApiDateTime(filters.datefrom)!});

            if (filters.dateto)
                params.push({key: 'dateto', value: toApiDateTime(filters.dateto, true)!});

            if (filters.userName) params.push({ key: 'userName', value: filters.userName });
            if (filters.actionType !== undefined && filters.actionType !== null)
                params.push({ key: 'actionType', value: filters.actionType });
            if (filters.fullName) params.push({ key: 'fullName', value: filters.fullName });

            let result = await getAll('/api/auditlogs/getall/',
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data as any;
            dataResults = dataResults["items"] as IAuditLog[];
            setTotal(result?.data.totalCount);
            setTotalPages(Math.ceil((result?.data.totalCount ?? 0) / result?.data.pageSize));

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setAuditLogs(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate('/');
        }
    }

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    return (
        <div className="gg-auditLogList container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('AuditLogList')} 
                ]}/>

            <h3 className="mb-3">{t('AuditLogList')}</h3>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm}) => (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('UserName')}
                                        <Field className="form-control" name="userName"/>
                                    </label>
                                </div> 
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('FullName')}
                                        <Field className="form-control" name="fullName"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('DateFrom')}
                                        <FormikDatePicker name="datefrom" showTime placeholderText={t('ChooseDate')}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('DateTo')}
                                        <FormikDatePicker name="dateto" showTime endOfDayDefault placeholderText={t('ChooseDate')}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ActionType')}
                                        <FormikSelect
                                            name="actionType"
                                            options={actionTypeOptions}
                                            isClearable
                                            clearValue={null}
                                            placeholder={`-- ${t('SelectOption')} --`}
                                        />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('UserId')}
                                        <Field className="form-control" name="userid"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3 align-self-end">
                                        <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                            { t('Search')}
                                        </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState);
                                            }}>
                                        { t('Reset')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            {loading && (<Loader />)}

            <div className="table-responsive border p-1 rounded-2">
                <table className="table table-sm table-striped-columns small table-logs">
                    <thead>
                    <tr>
                        <th style={{width: "30%"}} className="text-nowrap">{t('UserName')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('FullName')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('ChangedBy')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('IpAddress')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('ClientAppId')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('ActionType')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('EntityType')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('SourceSystem')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('Reason')}</th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("timestamp")}>{t('Timestamp')}
                            <SortDirection isActive={sortField === 'timestamp'} order={order}/>
                        </th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('ErrorMessage')}</th>
                        <th style={{width: "30%"}} onClick={() => handleSort("userId")}>{t('UserId')}
                            <SortDirection isActive={sortField === 'userId'} order={order}/>
                        </th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {auditLogs.map(log => (
                        <tr key={log.auditId} className={'table-logs__item ' + (log.errorMsg ? 'table-logs__item--error' : '')}>
                            <td className="text-nowrap">
                                <NavLink to={'/audit-logs/details/' + log.auditId}>{log.userName && log.userName.length > 0 ? log.userName: <i className="text-danger">{t('UserRemoved')}</i>}</NavLink>
                            </td>
                            <td className="text-nowrap">
                                {log.fullName && log.fullName.length > 0 && 
                                    <NavLink to={'/audit-logs/details/' + log.auditId}>{log.fullName}</NavLink>}
                            </td>
                            <td className="text-nowrap">
                                {log.actionType !== IAuditActionType.Login && log.actionType !== IAuditActionType.Verify
                                    ? (log.actorFullName || log.actorUserName || null)
                                    : null}
                            </td>
                            <td className="text-nowrap">{log.ipAddress}</td>
                            <td className="text-nowrap">{log.clientAppId}</td>
                            <td className="text-nowrap">{AuditActionTypeEnumText[log.actionType]}</td>
                            <td className="text-nowrap">{AuditEntityTypeEnumText[log.entityType]}</td>
                            <td className="text-nowrap">{AuditSourceSystemEnumText[log.sourceSystem]}</td>
                            <td className="text-nowrap">{log.reason}</td>
                            <td className="text-nowrap">{formatDateTime(log.timestamp)}</td>
                            <td className="text-nowrap">{log.errorMsg}</td>
                            <td className="text-nowrap">
                                <NavLink to={'/audit-logs/details/' + log.auditId}>{log.userId}</NavLink>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <Pagination currentPage={page}
                                            onPageChange={(page) => { setPage(page) }}
                                            pageSize={pageSize}
                                            onPageSizeChange={(size) => setPageSize(size)}
                                            totalItems={total} />
        </div>
    )
}
export default AuditLogList;